import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DownOutlined,
  UpOutlined,
  CheckOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { MultiSelectProps } from '../types/common';
import { useTheme } from '../contexts/ThemeContext';

/** Prefer keeping the menu inside the viewport (~18rem min panel + page padding). */
const MENU_EDGE_PAD_PX = 16;
const MENU_MIN_WIDTH_PX = 288;

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  className = '',
  disableSearch = false,
  singleSelect = false,
  allowClear = false,
  fillRowHeight = false,
  compact = false,
  menuAlign = 'auto',
  showSelectedLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [resolvedMenuAlign, setResolvedMenuAlign] = useState<'start' | 'end'>(
    menuAlign === 'end' ? 'end' : 'start',
  );
  const { resolvedTheme } = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const close = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  useLayoutEffect(() => {
    if (!isOpen || !rootRef.current) return;

    if (menuAlign === 'start' || menuAlign === 'end') {
      setResolvedMenuAlign(menuAlign);
      return;
    }

    const rect = rootRef.current.getBoundingClientRect();
    const spaceRight = window.innerWidth - rect.left - MENU_EDGE_PAD_PX;
    const spaceLeft = rect.right - MENU_EDGE_PAD_PX;
    // Prefer start; flip to end when the panel would overflow the right edge.
    const next =
      spaceRight < MENU_MIN_WIDTH_PX && spaceLeft > spaceRight ? 'end' : 'start';
    setResolvedMenuAlign(next);
  }, [isOpen, menuAlign]);

  useEffect(() => {
    if (!isOpen) return;

    // Autofocus without scrolling the overflow parent (page shift).
    searchInputRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen]);

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return options;

    const filtered: typeof options = [];
    let lastGroup: (typeof options)[0] | null = null;

    options.forEach((option) => {
      const isGroup =
        option.isGroup || String(option.value).startsWith('__group__');
      const isSeparator = String(option.value).startsWith('__separator__');

      if (isGroup && !isSeparator) {
        lastGroup = option;
      } else if (isSeparator) {
        return;
      } else if (
        option.label &&
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        if (
          lastGroup &&
          (filtered.length === 0 || !filtered[filtered.length - 1].isGroup)
        ) {
          filtered.push(lastGroup);
          lastGroup = null;
        }
        filtered.push(option);
      }
    });

    return filtered;
  }, [options, searchTerm]);

  const handleOptionClick = (
    optionValue: string | number,
    isGroup?: boolean,
  ) => {
    if (
      isGroup ||
      String(optionValue).startsWith('__group__') ||
      String(optionValue).startsWith('__separator__')
    ) {
      return;
    }

    const isSelected = value.includes(optionValue);
    const newValue = singleSelect
      ? isSelected
        ? []
        : [optionValue]
      : isSelected
        ? value.filter((v: string | number) => v !== optionValue)
        : [...value, optionValue];
    onChange(newValue);
    if (singleSelect) close();
  };

  const selectedLabels = options
    .filter(
      (option: {
        value: string | number;
        label: string;
        isGroup?: boolean;
      }) =>
        !option.isGroup &&
        !String(option.value).startsWith('__group__') &&
        !String(option.value).startsWith('__separator__') &&
        value.includes(option.value),
    )
    .map(
      (option: { value: string | number; label: string }) =>
        option.label || 'Unknown',
    );

  // Single-select filters should show the chosen option name (e.g. "Account"),
  // not "Type · 1". Multi-select keeps the count pattern unless overridden.
  const preferSelectedLabel = showSelectedLabel === true ||
    (showSelectedLabel !== false && singleSelect);
  const triggerLabel =
    selectedLabels.length === 0
      ? placeholder
      : preferSelectedLabel
        ? selectedLabels.length === 1
          ? selectedLabels[0]
          : selectedLabels.join(', ')
        : `${placeholder} · ${selectedLabels.length}`;

  const rootClass = fillRowHeight
    ? `relative flex flex-col flex-1 min-h-0 ${className}`
    : `relative min-w-0 ${className}`;

  const padClass = compact
    ? `h-[38px] px-2.5 text-sm ${
        allowClear && value.length > 0 ? 'pr-14' : 'pr-8'
      }`
    : `px-3 py-3 ${allowClear && value.length > 0 ? 'pr-16' : 'pr-10'}`;

  const triggerClass = fillRowHeight
    ? `w-full border rounded-lg cursor-pointer transition-all flex flex-1 min-h-[52px] items-center px-3 py-3 ${
        allowClear && value.length > 0 ? 'pr-16' : 'pr-10'
      } ${
        resolvedTheme === 'dark'
          ? 'border-gray-600 bg-gray-800 text-gray-100 focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400'
          : 'border-gray-300 bg-white text-gray-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'
      }`
    : `w-full border ${compact ? 'rounded-md' : 'rounded-lg'} cursor-pointer transition-all ${padClass} ${
        resolvedTheme === 'dark'
          ? 'border-gray-600 bg-gray-800 text-gray-100 focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400'
          : 'border-gray-300 bg-white text-gray-900 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500'
      }`;

  const rowClass = fillRowHeight
    ? 'flex w-full min-w-0 items-center justify-between gap-2'
    : 'flex h-full min-w-0 items-center justify-between gap-2';

  return (
    <div ref={rootRef} className={rootClass.trim()}>
      <div
        className={triggerClass}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        <div className={rowClass}>
          <span
            className={`block min-w-0 ${
              selectedLabels.length > 0
                ? resolvedTheme === 'dark'
                  ? 'text-gray-100'
                  : 'text-gray-900'
                : resolvedTheme === 'dark'
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            {triggerLabel}
          </span>
        </div>
        {allowClear && value.length > 0 && (
          <button
            type="button"
            tabIndex={0}
            aria-label="Clear selection"
            className={`absolute top-1/2 z-20 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 ${
              compact ? 'right-7' : 'right-9'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            <CloseCircleOutlined
              className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}
            />
          </button>
        )}
        <div
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 ${
            compact ? 'right-2' : 'right-3'
          }`}
        >
          {isOpen ? (
            <UpOutlined
              className={`${compact ? 'w-3.5 h-3.5' : 'w-5 h-5'} ${
                resolvedTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
          ) : (
            <DownOutlined
              className={`${compact ? 'w-3.5 h-3.5' : 'w-5 h-5'} ${
                resolvedTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
          )}
        </div>
      </div>

      {isOpen && (
        <div
          className={`absolute top-full z-50 mt-1 flex max-h-80 min-w-full w-max max-w-[min(20rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border shadow-lg ${
            resolvedMenuAlign === 'end' ? 'right-0 left-auto' : 'left-0 right-auto'
          } ${
            resolvedTheme === 'dark'
              ? 'border-gray-700 bg-gray-800'
              : 'border-gray-200 bg-white'
          }`}
        >
          {!disableSearch && (
            <div
              className={`sticky top-0 z-10 flex-shrink-0 border-b p-2.5 ${
                resolvedTheme === 'dark'
                  ? 'border-gray-700 bg-gray-800'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${placeholder.toLowerCase()}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`h-9 w-full rounded-md border px-3 text-sm focus:outline-none focus:ring-2 ${
                  resolvedTheme === 'dark'
                    ? 'border-gray-600 bg-gray-900 text-gray-100 placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400'
                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
                }`}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-1.5">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(
                (option: {
                  value: string | number;
                  label: string;
                  isGroup?: boolean;
                  country?: string;
                }) => {
                  const isGroup =
                    option.isGroup ||
                    String(option.value).startsWith('__group__');
                  const isSeparator = String(option.value).startsWith(
                    '__separator__',
                  );

                  if (isSeparator) {
                    return (
                      <div
                        key={option.value}
                        className={`my-1 border-t ${
                          resolvedTheme === 'dark'
                            ? 'border-gray-700'
                            : 'border-gray-200'
                        }`}
                      />
                    );
                  }

                  if (isGroup) {
                    return (
                      <div
                        key={option.value}
                        className={`px-3.5 py-2 text-xs font-semibold uppercase tracking-wide ${
                          resolvedTheme === 'dark'
                            ? 'bg-gray-900/50 text-gray-400'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {option.label}
                      </div>
                    );
                  }

                  return (
                    <button
                      type="button"
                      key={option.value}
                      className={`flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left transition-colors ${
                        resolvedTheme === 'dark'
                          ? 'hover:bg-gray-700'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleOptionClick(option.value, isGroup)}
                    >
                      <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                        {value.includes(option.value) && (
                          <CheckOutlined
                            className={`h-3.5 w-3.5 ${
                              resolvedTheme === 'dark'
                                ? 'text-blue-400'
                                : 'text-blue-600'
                            }`}
                          />
                        )}
                      </div>
                      <span
                        className={`text-sm leading-snug break-words ${
                          resolvedTheme === 'dark'
                            ? 'text-gray-100'
                            : 'text-gray-900'
                        }`}
                      >
                        {option.label || 'Unknown'}
                      </span>
                    </button>
                  );
                },
              )
            ) : (
              <div
                className={`px-3.5 py-3 text-sm ${
                  resolvedTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
